import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Share2, Sparkles, Copy, ArrowRight, DollarSign, Users, Zap, ExternalLink, Loader2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageSeo from "@/hooks/usePageSeo";

/**
 * Public /affiliate — creator self-serve application form.
 *
 * Form → POST /api/affiliates/apply → returns the unique slug immediately →
 * we render a success screen that includes their shareable link + copy button.
 * Applications are flagged `pending_review: true` server-side so admins can
 * eyeball each one (adjusting commission or deactivating spam) but the
 * applicant can start driving traffic in the meantime.
 *
 * Legal: acceptance checkbox links to the Affiliate T&Cs published in Trust
 * Center (Admin can seed / upload the actual PDF at any time).
 */
export default function AffiliateApply() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [audience, setAudience] = useState("");
  const [handle, setHandle] = useState("");
  const [pitch, setPitch] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  usePageSeo({
    title: "Become a DPP Affiliate — Refer Dividend Investors, Earn Monthly",
    description:
      "Join the DPP affiliate program. Unique tracking link, recurring commission, and a live dashboard for every signup you drive.",
    path: "/affiliate",
    ogSlug: "affiliate",
    ogType: "website",
    jsonLdId: "affiliate-apply-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Become a DPP Affiliate",
      description: "Apply to the Dividend Portfolio Planner affiliate program — MAU-based recurring commissions.",
      mainEntity: {
        "@type": "Offer",
        name: "DPP Affiliate Program",
        description: "$1 per Monthly Active User you refer to DPP, paid monthly. Applies to Free and Pro users alike.",
      },
    },
  });

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { toast.error("Please accept the Affiliate Terms to apply."); return; }
    if (!name.trim() || !email.trim()) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/affiliates/apply", {
        name: name.trim(),
        email: email.trim(),
        audience_size: audience.trim() || undefined,
        social_handle: handle.trim() || undefined,
        pitch: pitch.trim() || undefined,
        accepted_terms: accepted,
        website,  // honeypot; empty for real humans
      });
      setResult(r.data);
      toast.success(r.data.duplicate ? "Welcome back — your link is below" : "Application received!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  // Success screen
  if (result && result.slug) {
    const link = `${origin}/?aff=${result.slug}`;
    const dashUrl = result.dashboard_token ? `${origin}/affiliate/dashboard/${result.dashboard_token}` : null;
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="affiliate-success">
        <CheckCircle2 className="w-10 h-10 text-primary mx-auto" />
        <h1 className="serif text-4xl mt-4">
          {result.duplicate ? "Welcome back" : "You're in."}
        </h1>
        <p className="text-muted-foreground mt-3 max-w-lg mx-auto">
          {result.duplicate
            ? "We already have an affiliate account for this email — here's your existing link and dashboard."
            : "Your affiliate link is live. Share it anywhere — every click, signup, and paying user is tracked automatically."}
        </p>

        <Card className="mt-8 max-w-lg mx-auto border-primary/30">
          <CardContent className="p-5">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Your shareable link</div>
            <div className="rounded-md border border-border bg-muted/40 px-3 py-3 mono text-sm break-all" data-testid="affiliate-link-display">
              {link}
            </div>
            <div className="mt-3 flex gap-2 justify-center flex-wrap">
              <Button
                onClick={async () => { await navigator.clipboard.writeText(link); toast.success("Copied to clipboard"); }}
                className="rounded-full"
                data-testid="affiliate-link-copy"
              >
                <Copy className="w-4 h-4 mr-1.5" /> Copy link
              </Button>
              <Button variant="outline" asChild className="rounded-full">
                <a href={link} target="_blank" rel="noopener noreferrer">
                  Preview <ExternalLink className="w-4 h-4 ml-1.5" />
                </a>
              </Button>
            </div>
            {dashUrl && (
              <div className="mt-4 border-t border-border pt-4">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Your private dashboard</div>
                <div className="rounded-md border border-border bg-muted/40 px-3 py-3 mono text-xs break-all" data-testid="affiliate-dashboard-url">
                  {dashUrl}
                </div>
                <div className="mt-3 flex gap-2 justify-center flex-wrap">
                  <Button variant="outline" size="sm" onClick={async () => { await navigator.clipboard.writeText(dashUrl); toast.success("Dashboard URL copied — bookmark it!"); }} className="rounded-full" data-testid="affiliate-dashboard-copy">
                    <Copy className="w-4 h-4 mr-1.5" /> Copy dashboard URL
                  </Button>
                  <Button size="sm" asChild className="rounded-full">
                    <a href={dashUrl}>Open my dashboard →</a>
                  </Button>
                </div>
                <div className="mono text-[10px] text-muted-foreground mt-2">
                  Bookmark this — it's your only way in. No login required. Track UTM per post by appending <code>&amp;utm_content=your-post</code> to your share link.
                </div>
              </div>
            )}
            {result.pending_review && !result.duplicate && (
              <div className="mt-4 text-xs text-muted-foreground text-left rounded-md bg-muted/40 p-3">
                <b>Pending review:</b> your account is active and tracking clicks/signups today. Our team will confirm your commission tier and reach out at <span className="mono">{email}</span> within 48 hours.
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-8 flex gap-3 justify-center flex-wrap">
          <Button variant="outline" onClick={() => nav("/")} className="rounded-full">
            Explore DPP <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="affiliate-apply-page">
      {/* Hero */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-secondary">
          <Share2 className="w-3.5 h-3.5" /> Affiliate program
        </div>
        <h1 className="serif text-4xl sm:text-5xl lg:text-6xl mt-3" data-testid="affiliate-hero-title">
          Refer dividend investors. Earn monthly.
        </h1>
        <p className="text-muted-foreground mt-4">
          $1 per Monthly Active User you send to DPP — paid every month, for as long as they keep opening the app.
          Free tier or Pro, doesn't matter. Simple, recurring, no thresholds.
        </p>
      </div>

      {/* Value pills */}
      <div className="mt-8 grid sm:grid-cols-3 gap-3">
        <ValuePill icon={DollarSign} title="$1 per MAU / month" body="Every referred user who opens DPP in the last 30 days pays out." />
        <ValuePill icon={Zap}        title="Instant link" body="Fill the form, get your `/?aff=you` link on the confirmation screen." />
        <ValuePill icon={Users}      title="Recurring, not one-shot" body="Traditional referrals pay once. DPP pays as long as your audience uses the product." />
      </div>

      {/* Form */}
      <Card className="mt-10">
        <CardContent className="p-6 sm:p-8">
          <h2 className="serif text-2xl">Apply in 60 seconds</h2>
          <p className="text-sm text-muted-foreground mt-1">We review every application within 48 hours. Approved partners get a bump to their commission tier — but you can start driving traffic today.</p>
          <form onSubmit={submit} className="mt-5 space-y-4">
            {/* Honeypot — visually hidden but focusable by bots */}
            <div style={{position:"absolute", left:"-10000px", top:"auto", width:"1px", height:"1px", overflow:"hidden"}} aria-hidden="true">
              <label>Website (leave empty)</label>
              <input tabIndex="-1" autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Your name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Creator" required data-testid="affiliate-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" required data-testid="affiliate-email" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Primary handle / channel</Label>
                <Input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@janedividends" data-testid="affiliate-handle" />
              </div>
              <div>
                <Label>Audience size</Label>
                <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g. 12K YouTube subs" data-testid="affiliate-audience" />
              </div>
            </div>
            <div>
              <Label>Pitch (optional)</Label>
              <Textarea rows={3} value={pitch} onChange={(e) => setPitch(e.target.value)} placeholder="Tell us what you cover, where you'd share the link, and why it fits DPP." data-testid="affiliate-pitch" />
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="affiliate-terms-wrap">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5" data-testid="affiliate-accept-terms" />
              <span>
                I agree to the{" "}
                <Link to="/trust?doc=affiliate-terms" target="_blank" className="underline underline-offset-2 text-primary">DPP Affiliate Program Terms &amp; Conditions</Link>
                {" "}and the{" "}
                <Link to="/trust?doc=terms-of-service" target="_blank" className="underline underline-offset-2 text-primary">Terms of Service</Link>.
              </span>
            </label>

            <Button type="submit" disabled={saving || !accepted} className="rounded-full" data-testid="affiliate-apply-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : <><Sparkles className="w-4 h-4 mr-2" /> Apply and get my link</>}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Fine print */}
      <div className="mt-10 text-xs text-muted-foreground max-w-2xl mx-auto space-y-2">
        <p><b>Payout mechanics.</b> Commissions accrue monthly based on your referred users' 30-day activity. Payouts issued via bank transfer or PayPal on the 1st of each month for balances ≥ $25. Sub-threshold balances roll over.</p>
        <p><b>Attribution.</b> Cookie + localStorage sticky attribution until first signup. If a visitor clicks two affiliate links, last-click wins.</p>
        <p><b>Fair play.</b> We reserve the right to reject or claw back commission on fraudulent activity (fake accounts, bot traffic, self-referral). See full T&Cs.</p>
      </div>
    </div>
  );
}

function ValuePill({ icon: Icon, title, body }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="w-4 h-4" />
      </div>
      <div className="serif text-lg mt-2">{title}</div>
      <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{body}</div>
    </div>
  );
}
